// An assistant's plan applied to the timeline (#242 part 2, spec G11 "Applying" and
// docs/specs/editor/assistant.md): each step its actions as the editor's own edits, made as one
// undo step, in order. Before a step is made, each of its actions is tried on the timeline as it
// is by then; an action that cannot be made (a clip unknown or locked, no room, not a cut, or
// nothing it would change) fails the step with the reducer's reason (`refusal`, #601), and
// nothing after it runs (live's executor). A `@ref` names a clip an earlier action made: each gets
// a fresh id. Auto Captions (#242) is checked here and run by the editor's caption run once the
// plan's edits are made, and a Rough Cut (#1001) after them on the words then. A plan that
// searches the library holds its searches alone, which the editor runs before it asks again
// (spec assistant-search). Pure.
import type { AssistAction, AssistPlan, AssistStep, EffectKind } from "../../../api/generated/model";
import { MAX_ANIMATIONS, newAnimation, PRESETS, type Animation } from "../animations";
import { FILTER_PARAMS, heldValue, isFilter, type EffectValue, type FilterParam } from "../effect-filters";
import type { TextStyle, TextStylePatch } from "../text-style";
import { CAPTIONS_BUSY, noCaptionRun } from "../captions/transcribe";
import { offeredKey, type OfferedFile } from "./assist-media";
import { MAX_SEARCHES, SEARCH, type LibrarySearch } from "./library-search";
import { presetEasing, presetIterations } from "./preset-defaults";
import { LIVE_ROUGH_CUT, ROUGH_CUT, type RoughCutOptions } from "../rough-cut";
import {
  editTimeline,
  junctionsOf,
  refusal,
  REFUSALS,
  UNCHANGED,
  type Clip,
  type ClipPatch,
  type Timeline,
  type TimelineEdit,
} from "../timeline";
import { sameContent } from "../wire";

export interface ApplyContext {
  /** A video's or a sound's file length, when known (a trim's limit). */
  sourceMs: (clip: Clip) => number | null;
  /** A new title's look: the editor's "Add Text" (G11: the default style). */
  titleStyle: TextStyle;
  newId: () => string;
  /** The files the assistant was offered (G11 PR 2), by `offeredKey`: `add_clip` adds only these. */
  media: ReadonlyMap<string, OfferedFile>;
  /** The editor's caption run is going (#239): an Auto Captions cannot start another. */
  captionsBusy: boolean;
  /** The answer is a library search's follow-up (spec assistant-search): it searches no more. */
  searched?: boolean;
}

/**
 * What became of a step: made (its one edit, or none when it only asks for captions or a rough
 * cut; whether it asks the editor's caption run for them; and its rough cut's options, made after
 * the plan's edits and its caption run; its library searches, run before the editor asks again),
 * or failed with its reason.
 */
export type StepOutcome =
  | {
      step: AssistStep;
      status: "done";
      edit: TimelineEdit | null;
      captions: boolean;
      roughCut: RoughCutOptions | null;
      searches: readonly LibrarySearch[];
    }
  | { step: AssistStep; status: "failed"; reason: string };

/** An action as an edit, or why it cannot be one; refs the action makes are written to `refs`. */
type Made = { edits: TimelineEdit[] } | { reason: string };

class Refused extends Error {}

/**
 * The plan's steps on `timeline`, in order: each done step's edit (made by the caller as one undo
 * step) and the timeline after it, until a step fails; the steps after a failed one are left out,
 * as live's executor stops there.
 */
export function applyPlan(timeline: Timeline, plan: AssistPlan, context: ApplyContext): { outcomes: StepOutcome[]; timeline: Timeline } {
  // A plan that searches the library makes no edit: its follow-up plans the rest (decision 2).
  const searching = (plan.steps ?? []).some((step) => step.actions.some((action) => action.type === "search_library"));
  if (searching && context.searched !== true) return { outcomes: searchesOf(plan), timeline };
  const refs = new Map<string, string>();
  const outcomes: StepOutcome[] = [];
  let current = timeline;
  // The plan's one caption run: a second waits for none and is refused, as a run going is.
  let captioned = false;
  // A rough cut cuts by words: the captions there, or the ones this plan's Auto Captions makes.
  const captionsPlanned = (plan.steps ?? []).some((step) => step.actions.some((action) => action.type === "auto_captions"));
  let roughCutting = false;
  for (const step of plan.steps ?? []) {
    let scratch = current;
    const edits: TimelineEdit[] = [];
    let reason: string | null = null;
    let captions = false;
    let roughCut: RoughCutOptions | null = null;
    for (const action of step.actions) {
      if (action.type === "auto_captions") {
        // Live's Auto Captions: refused as the editor's Generate is, else run after the edits.
        reason = captioned || context.captionsBusy ? CAPTIONS_BUSY.text : (noCaptionRun(scratch)?.text ?? null);
        if (reason !== null) break;
        captioned = captions = true;
        continue;
      }
      if (action.type === "search_library") {
        // Only a follow-up gets here: one round of searches a message (decision 3).
        reason = SEARCH.again;
        break;
      }
      if (action.type === "rough_cut") {
        // Live's Rough Cut, made by the panel once the plan's edits and its caption run are done.
        const worded = captionsPlanned || scratch.clips.some((clip) => clip.kind === "caption" && clip.words.length > 0);
        reason = roughCutting ? ROUGH_CUT.once : worded ? null : ROUGH_CUT.noCaptions;
        if (reason !== null) break;
        roughCutting = true;
        roughCut = {
          fillers: action.fillers ?? LIVE_ROUGH_CUT.fillers,
          minSilenceMs: action.min_silence_ms ?? LIVE_ROUGH_CUT.minSilenceMs,
          paddingMs: action.padding_ms ?? LIVE_ROUGH_CUT.paddingMs,
        };
        continue;
      }
      const made = madeOf(scratch, action, refs, context);
      if ("reason" in made) {
        reason = made.reason;
        break;
      }
      const next = made.edits.reduce(editTimeline, scratch);
      // Nothing it would change, as saved (a patch to the values a clip has makes a new object):
      // the reducer's reason for the first of its edits it refuses, else that it is so already.
      if (sameContent(next, scratch)) {
        reason = made.edits.map((edit) => refusal(scratch, edit)).find((each) => each !== null) ?? UNCHANGED;
        break;
      }
      edits.push(...made.edits);
      scratch = next;
    }
    if (reason !== null) {
      outcomes.push({ step, status: "failed", reason });
      break;
    }
    const edit: TimelineEdit | null = edits.length === 0 ? null : { type: "many", edits, alone: true };
    outcomes.push({ step, status: "done", edit, captions, roughCut, searches: [] });
    current = scratch;
  }
  return { outcomes, timeline: current };
}

/**
 * A searching plan's outcomes: each step that searches, done with its searches, in order, until one
 * would pass the round's five; the steps that do not search wait for the follow-up, not shown.
 */
function searchesOf(plan: AssistPlan): StepOutcome[] {
  const outcomes: StepOutcome[] = [];
  let count = 0;
  for (const step of plan.steps ?? []) {
    const searches: LibrarySearch[] = step.actions.flatMap((action) =>
      action.type === "search_library" ? [{ query: action.query, kind: action.kind ?? null, limit: action.limit ?? 1 }] : [],
    );
    if (searches.length === 0) continue;
    count += searches.length;
    if (count > MAX_SEARCHES) {
      outcomes.push({ step, status: "failed", reason: SEARCH.most });
      break;
    }
    outcomes.push({ step, status: "done", edit: null, captions: false, roughCut: null, searches });
  }
  return outcomes;
}

/** A clip's id from the plan: one of the timeline's, or a ref an earlier action made. */
function idOf(clipId: string, refs: ReadonlyMap<string, string>): string {
  if (!clipId.startsWith("@")) return clipId;
  const id = refs.get(clipId);
  if (id === undefined) throw new Refused(`${clipId} was not made by an earlier action.`);
  return id;
}

/** A ref the action makes: a fresh id, once a plan. */
function made(ref: string, refs: Map<string, string>, context: ApplyContext): string {
  if (refs.has(ref)) throw new Refused(`${ref} was made twice.`);
  const id = context.newId();
  refs.set(ref, id);
  return id;
}

/**
 * The clip an action names, in the reducer's words when it is not there or is locked. A locked
 * clip is refused for every action that names one but its unlock, a duplicate and a transition
 * too, which the editor's reducer allows (spec refusal.md, decision 5).
 */
function clipNamed(timeline: Timeline, id: string): Clip {
  const clip = timeline.clips.find((each) => each.id === id);
  if (clip === undefined) throw new Refused(REFUSALS.noClip(id));
  if (clip.locked) throw new Refused(REFUSALS.locked(id));
  return clip;
}

function madeOf(timeline: Timeline, action: AssistAction, refs: Map<string, string>, context: ApplyContext): Made {
  try {
    return { edits: editsOf(timeline, action, refs, context) };
  } catch (error) {
    if (error instanceof Refused) return { reason: error.message };
    throw error;
  }
}

function editsOf(timeline: Timeline, action: AssistAction, refs: Map<string, string>, context: ApplyContext): TimelineEdit[] {
  switch (action.type) {
    case "move_clip": {
      const id = clipNamed(timeline, idOf(action.clip_id, refs)).id;
      const trackId = action.track_id ?? undefined;
      return [{ type: "moveClip", id, startMs: action.start_ms, ...(trackId === undefined ? {} : { trackId }) }];
    }
    case "trim_start":
      return [{ type: "trimStart", id: clipNamed(timeline, idOf(action.clip_id, refs)).id, startMs: action.start_ms }];
    case "trim_end": {
      const clip = clipNamed(timeline, idOf(action.clip_id, refs));
      return [{ type: "trimEnd", id: clip.id, endMs: action.end_ms, sourceMs: context.sourceMs(clip) }];
    }
    case "set_range": {
      const clip = clipNamed(timeline, idOf(action.clip_id, refs));
      return [{ type: "setRange", id: clip.id, inMs: action.in_ms, outMs: action.out_ms, sourceMs: context.sourceMs(clip) }];
    }
    case "set_clip": {
      const id = clipNamed(timeline, idOf(action.clip_id, refs)).id;
      const patch: ClipPatch = {
        ...(action.muted == null ? {} : { muted: action.muted }),
        ...(action.volume == null ? {} : { volume: action.volume }),
        ...(action.fade_in_ms == null ? {} : { fadeInMs: action.fade_in_ms }),
        ...(action.fade_out_ms == null ? {} : { fadeOutMs: action.fade_out_ms }),
        ...(action.text == null ? {} : { text: action.text }),
        ...(action.hidden == null ? {} : { hidden: action.hidden }),
      };
      return [{ type: "setClip", id, patch }];
    }
    case "set_look": {
      const clip = clipNamed(timeline, idOf(action.clip_id, refs));
      if (clip.kind !== "video" && clip.kind !== "image") throw new Refused(`Clip ${clip.id} is not a video or a picture.`);
      return [{ type: "setClip", id: clip.id, patch: lookPatch(action) }];
    }
    case "set_text_style": {
      const clip = clipNamed(timeline, idOf(action.clip_id, refs));
      if (clip.kind !== "text") throw new Refused(`Clip ${clip.id} is not a text.`);
      return [{ type: "setClip", id: clip.id, patch: { style: stylePatch(action) } }];
    }
    case "add_animation": {
      const clip = animatedClip(timeline, idOf(action.clip_id, refs));
      const group = PRESETS[action.preset].group;
      if (group === "text" && clip.kind !== "text") throw new Refused(`${action.preset} is a text's animation: clip ${clip.id} is not a text.`);
      if (group === "combo" && clip.kind === "text") throw new Refused(`${action.preset} is a video's or a picture's animation: clip ${clip.id} is a text.`);
      if (clip.animations.length >= MAX_ANIMATIONS) throw new Refused(`Clip ${clip.id} holds the most animations it can (${MAX_ANIMATIONS}).`);
      const added = animationOf(action, context.newId(), clip.endMs - clip.startMs);
      return [{ type: "setClip", id: clip.id, patch: { animations: [...clip.animations, added] } }];
    }
    case "remove_animations": {
      const clip = animatedClip(timeline, idOf(action.clip_id, refs));
      const named = (each: Animation) =>
        (action.preset == null || each.preset === action.preset) && (action.mode == null || each.mode === action.mode);
      if (!clip.animations.some(named)) throw new Refused(`Clip ${clip.id} has no such animation.`);
      return [{ type: "setClip", id: clip.id, patch: { animations: clip.animations.filter((each) => !named(each)) } }];
    }
    case "set_effect_values": {
      const clip = clipNamed(timeline, idOf(action.clip_id, refs));
      if (clip.kind !== "effect") throw new Refused(`Clip ${clip.id} is not an effect.`);
      return [{ type: "setClip", id: clip.id, patch: { effectValues: effectValuesOf(clip.effect, action.values) } }];
    }
    case "remove_clip":
      return [{ type: "removeClip", id: clipNamed(timeline, idOf(action.clip_id, refs)).id }];
    case "split": {
      const id = clipNamed(timeline, idOf(action.clip_id, refs)).id;
      return [{ type: "split", id, atMs: action.at_ms, newId: made(action.ref, refs, context) }];
    }
    case "duplicate": {
      const id = clipNamed(timeline, idOf(action.clip_id, refs)).id;
      return [{ type: "duplicate", id, newId: made(action.ref, refs, context), newTrackId: context.newId() }];
    }
    case "separate_audio": {
      const id = clipNamed(timeline, idOf(action.clip_id, refs)).id;
      return [{ type: "separateAudio", id, newId: made(action.ref, refs, context), newTrackId: context.newId() }];
    }
    case "set_lock":
      // The one edit a locked clip takes; the reducer says when there is no such clip.
      return [{ type: "setLock", id: idOf(action.clip_id, refs), locked: action.locked }];
    case "add_title":
      return [
        {
          type: "addTitle",
          id: made(action.ref, refs, context),
          text: action.text,
          startMs: action.start_ms,
          endMs: action.end_ms,
          style: context.titleStyle,
          newTrackId: context.newId(),
        },
      ];
    case "add_effect":
      return [{ type: "addEffect", id: made(action.ref, refs, context), effect: action.effect, newTrackId: context.newId() }];
    case "add_clip":
      return clipAdded(action, refs, context);
    case "set_transition": {
      const fromId = clipNamed(timeline, idOf(action.from_clip_id, refs)).id;
      const toId = clipNamed(timeline, idOf(action.to_clip_id, refs)).id;
      // A cut's transition keeps its id; a new one gets one. The reducer says when there is no cut.
      const cut = junctionsOf(timeline).find((each) => each.fromId === fromId && each.toId === toId);
      const id = cut?.transition?.id ?? context.newId();
      const set: TimelineEdit = { type: "setTransition", id, fromId, toId, kind: action.kind };
      return action.duration_ms == null ? [set] : [set, { type: "setTransitionDuration", id, durationMs: action.duration_ms }];
    }
    case "remove_transition":
      // The reducer says when there is no such transition.
      return [{ type: "removeTransition", id: action.transition_id }];
    case "remove_captions":
      return captionsRemoved(timeline, action.from_ms ?? null, action.to_ms ?? null);
    case "rough_cut":
    case "auto_captions":
    case "search_library":
      // No edit of its own: `applyPlan` asks the editor's caption run for it, the panel makes the
      // rough cut after the plan's edits, and a search runs before the editor asks again.
      return [];
    case "set_aspect":
      // Live's ratio menu (G11 PR 2c): the frame's shape, every item keeping its pixels.
      return [{ type: "setAspect", aspect: action.aspect }];
    default:
      return action satisfies never;
  }
}

/** What each kind of control takes, as the plan says it. */
const TAKES: Record<FilterParam["kind"], string> = {
  range: "a number",
  choice: "one of its numbers",
  point: 'an {"x", "y"} pair',
  color: "a #RRGGBB color",
  flag: "true or false",
};

function takes(param: FilterParam, value: EffectValue): boolean {
  switch (param.kind) {
    case "range":
      return typeof value === "number";
    case "choice":
      return param.options.some((option) => option.value === value);
    case "point":
      return typeof value === "object" && typeof value.x === "number" && typeof value.y === "number";
    case "color":
      return typeof value === "string";
    case "flag":
      return typeof value === "boolean";
  }
}

/**
 * A `set_effect_values` as the editor's values (G11 PR 2e): each named setting of the filter, of
 * its control's kind, held to its range (`heldValue`, as the Configuration panel holds it). An
 * effect with no settings, a name it does not have, or a value of another kind is refused.
 */
function effectValuesOf(
  effect: EffectKind,
  values: Extract<AssistAction, { type: "set_effect_values" }>["values"],
): Record<string, EffectValue> {
  if (!isFilter(effect) || FILTER_PARAMS[effect].length === 0) throw new Refused(`The ${effect} effect has no settings.`);
  const params = FILTER_PARAMS[effect];
  return Object.fromEntries(
    values.map(({ name, value }) => {
      const param = params.find((each) => each.name === name);
      if (param === undefined) throw new Refused(`The ${effect} effect has no setting ${name}.`);
      if (!takes(param, value)) throw new Refused(`The ${effect} effect's ${name} takes ${TAKES[param.kind]}.`);
      return [name, heldValue(param, value)];
    }),
  );
}

/** A clip that takes animations: a video, a picture or a text. */
function animatedClip(timeline: Timeline, id: string): Extract<Clip, { kind: "video" | "image" | "text" }> {
  const clip = clipNamed(timeline, id);
  if (clip.kind !== "video" && clip.kind !== "image" && clip.kind !== "text") throw new Refused(`Clip ${clip.id} takes no animation.`);
  return clip;
}

/**
 * An `add_animation` as the editor's animation (G11 PR 2d): the preset as its popover starts it
 * (`newAnimation`: In, 1000 ms, a combo the whole clip) with live's assistant's easing and cycles
 * for it (`presetEasing`, `presetIterations`), then what the action gives over it, and Custom's
 * stops from their lists.
 */
function animationOf(action: Extract<AssistAction, { type: "add_animation" }>, id: string, clipMs: number): Animation {
  const popover = newAnimation(id, action.preset, clipMs);
  const mode = action.mode ?? popover.mode;
  const start = { ...popover, mode, easing: presetEasing(action.preset, mode), iterations: presetIterations(action.preset) };
  const stops = action.keyframes ?? [];
  return {
    ...start,
    ...(action.duration_ms == null ? {} : { durationMs: action.duration_ms }),
    ...(action.delay_ms == null ? {} : { delayMs: action.delay_ms }),
    ...(action.iterations == null ? {} : { iterations: action.iterations }),
    ...(action.easing == null ? {} : { easing: action.easing }),
    ...(action.direction == null ? {} : { direction: action.direction }),
    ...(action.distance == null ? {} : { distance: action.distance }),
    ...(action.stagger == null ? {} : { stagger: action.stagger }),
    ...(action.mirror == null ? {} : { mirror: action.mirror }),
    ...(stops.length === 0
      ? {}
      : {
          keyframes: stops.map((stop) => ({
            at: stop.at,
            values: Object.fromEntries((stop.values ?? []).map((each) => [each.property, each.value])),
          })),
        }),
  };
}

type WireShadow = { color: string; offset_x: number; offset_y: number; blur: number };

function shadowOf(shadow: WireShadow): { color: string; offsetX: number; offsetY: number; blur: number } {
  return { color: shadow.color, offsetX: shadow.offset_x, offsetY: shadow.offset_y, blur: shadow.blur };
}

/** A `set_look` as the editor's patch (G11 PR 2b): what it gives, and null for what it clears. */
function lookPatch(action: Extract<AssistAction, { type: "set_look" }>): ClipPatch {
  const clear = new Set(action.clear ?? []);
  return {
    ...(action.box == null ? {} : { box: action.box }),
    ...(action.rotation == null ? {} : { rotation: action.rotation }),
    ...(action.flip_x == null ? {} : { flipX: action.flip_x }),
    ...(action.flip_y == null ? {} : { flipY: action.flip_y }),
    ...(action.opacity == null ? {} : { opacity: action.opacity }),
    ...(action.radius == null ? {} : { radius: action.radius }),
    ...(action.stroke == null ? {} : { stroke: action.stroke }),
    ...(action.shadow == null ? {} : { shadow: shadowOf(action.shadow) }),
    ...(action.chroma_key == null ? {} : { chromaKey: action.chroma_key }),
    ...(clear.has("box") ? { box: null } : {}),
    ...(clear.has("stroke") ? { stroke: null } : {}),
    ...(clear.has("shadow") ? { shadow: null } : {}),
    ...(clear.has("chroma_key") ? { chromaKey: null } : {}),
  };
}

/** A `set_text_style` as the editor's style patch (G11 PR 2b), null for what it clears. */
function stylePatch(action: Extract<AssistAction, { type: "set_text_style" }>): TextStylePatch {
  const clear = new Set(action.clear ?? []);
  const background = action.background;
  return {
    ...(action.x == null ? {} : { x: action.x }),
    ...(action.y == null ? {} : { y: action.y }),
    ...(action.width == null ? {} : { width: action.width }),
    ...(action.rotation == null ? {} : { rotation: action.rotation }),
    ...(action.opacity == null ? {} : { opacity: action.opacity }),
    ...(action.font == null ? {} : { font: action.font }),
    ...(action.weight == null ? {} : { weight: action.weight }),
    ...(action.italic == null ? {} : { italic: action.italic }),
    ...(action.size == null ? {} : { size: action.size }),
    ...(action.align == null ? {} : { align: action.align }),
    ...(action.underline == null ? {} : { underline: action.underline }),
    ...(action.overline == null ? {} : { overline: action.overline }),
    ...(action.strikethrough == null ? {} : { strikethrough: action.strikethrough }),
    ...(action.case == null ? {} : { textCase: action.case }),
    ...(action.color == null ? {} : { color: action.color }),
    ...(background == null
      ? {}
      : {
          background: {
            color: background.color,
            opacity: background.opacity ?? 1,
            radius: background.radius ?? 4,
            padX: background.pad_x ?? 8,
            padY: background.pad_y ?? 4,
          },
        }),
    ...(action.stroke == null ? {} : { stroke: action.stroke }),
    ...(action.shadow == null ? {} : { shadow: shadowOf(action.shadow) }),
    ...(clear.has("background") ? { background: null } : {}),
    ...(clear.has("stroke") ? { stroke: null } : {}),
    ...(clear.has("shadow") ? { shadow: null } : {}),
  };
}

/**
 * One of the files offered (G11 PR 2a) added whole, as live's `clip.add`: from `start_ms` (0
 * without), on the first track of its kind free over its whole span, else on a new track of that
 * kind above every other. A file the assistant was not offered is refused: the plan names only
 * files the panel listed.
 */
function clipAdded(
  action: Extract<AssistAction, { type: "add_clip" }>,
  refs: Map<string, string>,
  context: ApplyContext,
): TimelineEdit[] {
  const file = context.media.get(offeredKey(action.source));
  if (file === undefined) throw new Refused("That file is not one the assistant was offered.");
  const id = made(action.ref, refs, context);
  return [
    {
      type: "addClip",
      id,
      kind: file.kind,
      source: file.source,
      sourceMs: file.durationMs,
      newTrackId: context.newId(),
      startMs: action.start_ms ?? 0,
    },
  ];
}

/**
 * The captions an action removes (G11, #242 captions): every unlocked one, as live's "Delete
 * captions" when none is locked; or each unlocked one shown between `fromMs` and `toMs`.
 */
function captionsRemoved(timeline: Timeline, fromMs: number | null, toMs: number | null): TimelineEdit[] {
  const captions = timeline.clips.filter((clip) => clip.kind === "caption");
  const ranged = fromMs !== null && toMs !== null;
  const shown = ranged ? captions.filter((clip) => clip.endMs > fromMs && clip.startMs < toMs) : captions;
  if (shown.length === 0) {
    throw new Refused(ranged ? `No caption is shown between ${fromMs} and ${toMs} ms.` : REFUSALS.noCaptions());
  }
  const unlocked = shown.filter((clip) => !clip.locked);
  if (unlocked.length === 0) throw new Refused("Those captions are locked: unlock them first.");
  if (!ranged && unlocked.length === captions.length) return [{ type: "removeCaptions" }];
  return unlocked.map((clip) => ({ type: "removeClip", id: clip.id }));
}
